// src/contexts/useReloadOnUnrecoveredSubscription.ts
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { logToSentry } from "utils/logger";

const RELOAD_GRACE_MS = 15_000;
const RELOAD_STORM_WINDOW_MS = 5 * 60_000;
const STORAGE_KEY = "zombieReloadAt";

type Params = {
	isSubscriptionBroken: boolean;
	appConnected: boolean;
	fridgeId: string;
};

/**
 * Last-resort recovery for a subscription that won't revive. When the stream stays broken WHILE the
 * network is up (the zombie signature) past RELOAD_GRACE_MS — a live frame would have cleared
 * isSubscriptionBroken — reload the page once (the automated version of the manual fix). Genuine offline
 * (appConnected false) is excluded; useNavigateHomeOnSustainedDisconnect owns that. A sessionStorage
 * storm-guard caps auto-reloads to one per RELOAD_STORM_WINDOW_MS so a boot-into-bad-state can't loop.
 */
export const useReloadOnUnrecoveredSubscription = ({ isSubscriptionBroken, appConnected, fridgeId }: Params): void => {
	useEffect(() => {
		if (!(isSubscriptionBroken && appConnected)) {
			return;
		}
		const timer = window.setTimeout(() => {
			// `last === 0` means "no prior auto-reload recorded" (missing/blank key) — allow the reload.
			// Compare explicitly rather than leaning on `Date.now() - 0` being large, so a stored 0 (or a
			// raised storm window) can't silently flip the first-reload decision.
			const last = Number(sessionStorage.getItem(STORAGE_KEY) ?? "0");
			if (last !== 0 && Date.now() - last < RELOAD_STORM_WINDOW_MS) {
				Sentry.captureException(
					new Error(`Zombie subscription unrecovered after a recent reload, fridge: ${fridgeId}`),
				);
				return;
			}
			sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
			logToSentry(`Reloading to recover an unrecovered subscription, fridge: ${fridgeId}`, "warning");
			window.location.reload();
		}, RELOAD_GRACE_MS);
		return () => window.clearTimeout(timer);
	}, [isSubscriptionBroken, appConnected, fridgeId]);
};
